import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';

const initialState = {
  subscribers: [],
  loading: false,
  error: null,
};

// Async Thunk: Fetch All Subscribers (Admin Only)
export const fetchSubscribers = createAsyncThunk(
  'subscriber/fetchSubscribers',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/subscribers');
      if (res.data.success) {
        return res.data.subscribers;
      }
      return rejectWithValue('Failed to fetch subscribers');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Subscribe to Newsletter (Client Facing)
export const subscribeToNewsletter = createAsyncThunk(
  'subscriber/subscribeToNewsletter',
  async (email, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/newsletter', { email });
      if (res.data.success) {
        return { success: true, message: res.data.message };
      }
      return rejectWithValue('Subscription failed');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Delete Subscriber (Admin Only)
export const removeSubscriber = createAsyncThunk(
  'subscriber/removeSubscriber',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/subscribers/${id}`);
      if (res.data.success) {
        return id;
      }
      return rejectWithValue('Failed to delete subscriber');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Broadcast Newsletter to all Subscribers (Admin Only)
export const broadcastNewsletter = createAsyncThunk(
  'subscriber/broadcastNewsletter',
  async ({ subject, message }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/subscribers/broadcast', { subject, message });
      if (res.data.success) {
        return res.data;
      }
      return rejectWithValue('Broadcast failed');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Update Subscriber Status (Admin Only)
export const updateSubscriberStatus = createAsyncThunk(
  'subscriber/updateSubscriberStatus',
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/subscribers/${id}/status`, { isActive });
      if (res.data.success) {
        return { id, isActive };
      }
      return rejectWithValue('Failed to update subscriber status');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const subscriberSlice = createSlice({
  name: 'subscriber',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Subscribers
      .addCase(fetchSubscribers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscribers.fulfilled, (state, action) => {
        state.subscribers = action.payload;
        state.loading = false;
      })
      .addCase(fetchSubscribers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Subscriber
      .addCase(removeSubscriber.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeSubscriber.fulfilled, (state, action) => {
        state.subscribers = state.subscribers.filter(s => s._id !== action.payload);
        state.loading = false;
      })
      .addCase(removeSubscriber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Broadcast Newsletter
      .addCase(broadcastNewsletter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(broadcastNewsletter.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(broadcastNewsletter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Subscriber Status
      .addCase(updateSubscriberStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSubscriberStatus.fulfilled, (state, action) => {
        state.subscribers = state.subscribers.map(s => {
          if (s._id === action.payload.id || s.id === action.payload.id) {
            return { ...s, isActive: action.payload.isActive };
          }
          return s;
        });
        state.loading = false;
      })
      .addCase(updateSubscriberStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export default subscriberSlice.reducer;
